/**
 * Toast - Radix UI Toast primitives with vanilla-extract styling
 *
 * This file provides the low-level Toast components. For most use cases,
 * use the useToast hook from ToastProvider instead.
 *
 * @example
 * // Using the hook (recommended)
 * const { showSuccessToast } = useToast()
 * showSuccessToast('Changes saved!')
 *
 * // Using components directly (advanced)
 * <Toast variant="success">
 *   <Toast.Title>Success</Toast.Title>
 *   <Toast.Description>Your changes have been saved.</Toast.Description>
 * </Toast>
 */

import * as React from 'react'
import * as ToastPrimitive from '@radix-ui/react-toast'
import * as styles from './Toast.css'
import type { ToastVariants } from './Toast.css'

// =============================================================================
// Root
// =============================================================================

type ToastRootProps = React.ComponentPropsWithoutRef<typeof ToastPrimitive.Root> &
  NonNullable<ToastVariants> & {
    /** Show close button (default: true) */
    showClose?: boolean
  }

const ToastRoot = React.forwardRef<
  React.ComponentRef<typeof ToastPrimitive.Root>,
  ToastRootProps
>(({ className, variant, showClose = true, children, ...props }, ref) => (
  <ToastPrimitive.Root
    ref={ref}
    className={`${styles.root({ variant })} ${className ?? ''}`}
    {...props}
  >
    {children}
    {showClose && <ToastClose />}
  </ToastPrimitive.Root>
))
ToastRoot.displayName = 'Toast.Root'

// =============================================================================
// Title
// =============================================================================

const ToastTitle = React.forwardRef<
  React.ComponentRef<typeof ToastPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Title
    ref={ref}
    className={`${styles.title} ${className ?? ''}`}
    {...props}
  />
))
ToastTitle.displayName = 'Toast.Title'

// =============================================================================
// Description
// =============================================================================

const ToastDescription = React.forwardRef<
  React.ComponentRef<typeof ToastPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Description
    ref={ref}
    className={`${styles.description} ${className ?? ''}`}
    {...props}
  />
))
ToastDescription.displayName = 'Toast.Description'

// =============================================================================
// Action
// =============================================================================

type ToastActionProps = React.ComponentPropsWithoutRef<typeof ToastPrimitive.Action>

const ToastAction = React.forwardRef<
  React.ComponentRef<typeof ToastPrimitive.Action>,
  ToastActionProps
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Action
    ref={ref}
    className={`${styles.action} ${className ?? ''}`}
    {...props}
  />
))
ToastAction.displayName = 'Toast.Action'

// =============================================================================
// Close
// =============================================================================

const ToastClose = React.forwardRef<
  React.ComponentRef<typeof ToastPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Close
    ref={ref}
    className={`${styles.close} ${className ?? ''}`}
    aria-label="Close"
    {...props}
  >
    <span aria-hidden>×</span>
  </ToastPrimitive.Close>
))
ToastClose.displayName = 'Toast.Close'

// =============================================================================
// Types
// =============================================================================

export type ToastVariant = 'default' | 'success' | 'error' | 'info' | 'warning'

// =============================================================================
// Compound Export
// =============================================================================

export const Toast = Object.assign(ToastRoot, {
  Root: ToastRoot,
  Title: ToastTitle,
  Description: ToastDescription,
  Action: ToastAction,
  Close: ToastClose,
})

// Re-export types
export type { ToastRootProps, ToastActionProps }
